import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useEditorStore } from "@/store/editorStore";

/**
 * Hook to manage document title and meta tags based on project name
 * Updates the title to "360Crafter: PROJECTNAME" only when in a project context
 * Shows just "360Crafter" on other pages like Dashboard, Auth, Settings, etc.
 */
export const useDocumentTitle = () => {
  const { project } = useEditorStore();
  const location = useLocation();

  useEffect(() => {
    const baseTitle = "360Crafter";
    const baseDescription = "Create immersive 360° experiences";

    // Check if we're in a project context
    // Project routes are like /prj9tgkoozqo, /prj9tgkoozqo/editor, etc.
    // We detect this by checking if the path doesn't start with known non-project routes
    const nonProjectRoutes = ["/dashboard", "/login", "/craftspace"];
    const isProjectContext = !nonProjectRoutes.some((route) => location.pathname.startsWith(route));

    if (project?.name && isProjectContext) {
      const fullTitle = `${project.name} | 360Crafter`;
      const fullDescription = `${baseDescription} - ${project.name}`;

      // Update document title
      document.title = fullTitle;

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute("content", fullDescription);
      }

      // Update Open Graph tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute("content", fullTitle); // Just project name
      }

      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) {
        ogDescription.setAttribute("content", fullDescription);
      }
    } else {
      // Set title based on the current page
      let pageTitle = baseTitle;
      if (location.pathname.startsWith("/dashboard")) {
        pageTitle = `Dashboard | ${baseTitle}`;
      }
      document.title = pageTitle;

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute("content", baseDescription);
      }

      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) {
        ogTitle.setAttribute("content", baseTitle);
      }

      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) {
        ogDescription.setAttribute("content", baseDescription);
      }
    }
  }, [project?.name, location.pathname]);

  return project?.name;
};
